/**
 * OverlapStatCards Component
 *
 * Three dark stat cards showing Unique A, Shared, and Unique B counts.
 * Each card has: icon, count, label, percentage, and a colored progress bar.
 * Inspired by the Gemini reference design.
 */

import { User, Users, UserCheck } from 'lucide-react'

interface OverlapStatCardsProps {
  readonly subjectAName: string
  readonly subjectBName: string
  readonly totalA: number
  readonly totalB: number
  readonly overlapCount: number
  readonly percentA: number
  readonly percentB: number
  readonly className?: string
}

function formatCompact(value: number): string {
  const absValue = Math.abs(value)
  if (absValue >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (absValue >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return value.toLocaleString()
}

export function OverlapStatCards({
  subjectAName,
  subjectBName,
  totalA,
  totalB,
  overlapCount,
  percentA,
  percentB,
  className = '',
}: OverlapStatCardsProps) {
  const uniqueA = totalA - overlapCount
  const uniqueB = totalB - overlapCount
  const percentUniqueA = totalA > 0 ? (uniqueA / totalA) * 100 : 0
  const percentUniqueB = totalB > 0 ? (uniqueB / totalB) * 100 : 0

  return (
    <div className={`grid grid-cols-1 gap-4 md:grid-cols-3 ${className}`}>
      {/* Card A — Unique to Subject A */}
      <div className="bg-v11-surface border-2 border-blue-500/40 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-blue-500/20 text-blue-500">
              A
            </div>
            <div className="p-2 bg-blue-500/15 rounded-lg">
              <User className="h-5 w-5 text-blue-500" />
            </div>
          </div>
          <span className="text-2xl font-bold text-v11-text-primary">
            {uniqueA.toLocaleString()}
          </span>
        </div>
        <div className="text-xs font-semibold text-v11-text-muted uppercase tracking-wide">
          Unique to {subjectAName}
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-xl font-bold text-blue-500">
            {percentUniqueA.toFixed(1)}%
          </span>
          <span className="text-xs text-v11-text-muted">
            Total base: {formatCompact(totalA)}
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 w-full rounded-full bg-v11-surface-hover overflow-hidden">
          <div
            className="h-full rounded-full bg-blue-500 transition-all duration-500"
            style={{ width: `${Math.min(percentUniqueA, 100)}%` }}
          />
        </div>
      </div>

      {/* Card Shared — Overlap (orange = intersection color) */}
      <div className="bg-v11-surface border-2 border-orange-500/40 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="p-2 bg-orange-500/15 rounded-lg">
            <Users className="h-5 w-5 text-orange-500" />
          </div>
          <span className="text-2xl font-bold text-v11-text-primary">
            {overlapCount.toLocaleString()}
          </span>
        </div>
        <div className="text-xs font-semibold text-v11-text-muted uppercase tracking-wide">
          Shared Audience
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-semibold text-blue-500">
            {percentA.toFixed(1)}%{' '}
            <span className="text-xs font-normal text-v11-text-muted">Reach of A</span>
          </span>
          <span className="text-sm font-semibold text-purple-500">
            {percentB.toFixed(1)}%{' '}
            <span className="text-xs font-normal text-v11-text-muted">Reach of B</span>
          </span>
        </div>
        {/* Split progress bar */}
        <div className="h-1.5 w-full rounded-full bg-v11-surface-hover overflow-hidden flex">
          <div
            className="h-full bg-blue-500 transition-all duration-500"
            style={{ width: `${Math.min(percentA, 100)}%` }}
          />
          <div
            className="h-full bg-purple-500 transition-all duration-500 ml-auto"
            style={{ width: `${Math.min(percentB, 100)}%` }}
          />
        </div>
      </div>

      {/* Card B — Unique to Subject B */}
      <div className="bg-v11-surface border-2 border-purple-500/40 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-purple-500/20 text-purple-500">
              B
            </div>
            <div className="p-2 bg-purple-500/15 rounded-lg">
              <UserCheck className="h-5 w-5 text-purple-500" />
            </div>
          </div>
          <span className="text-2xl font-bold text-v11-text-primary">
            {uniqueB.toLocaleString()}
          </span>
        </div>
        <div className="text-xs font-semibold text-v11-text-muted uppercase tracking-wide">
          Unique to {subjectBName}
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-xl font-bold text-purple-500">
            {percentUniqueB.toFixed(1)}%
          </span>
          <span className="text-xs text-v11-text-muted">
            Total base: {formatCompact(totalB)}
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 w-full rounded-full bg-v11-surface-hover overflow-hidden">
          <div
            className="h-full rounded-full bg-purple-500 transition-all duration-500"
            style={{ width: `${Math.min(percentUniqueB, 100)}%` }}
          />
        </div>
      </div>
    </div>
  )
}

export default OverlapStatCards
